import { useTheme } from 'next-themes'
import { Toaster as Sonner, toast as sonnerToast } from 'sonner'

type ToasterProps = React.ComponentProps<typeof Sonner>
type ToastOptions = Parameters<typeof sonnerToast>[1]

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      richColors
      toastOptions={{
        classNames: {
          toast:
            'group toast relative overflow-hidden border border-border bg-background text-foreground rounded-md p-6 shadow-lg',
          title: 'text-sm font-semibold',
          description: 'text-sm text-muted-foreground opacity-90',
          actionButton:
            'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
          cancelButton:
            'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-muted px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
          success: 'border-emerald-500/50',
          error: 'border-destructive bg-destructive text-destructive-foreground',
          warning: 'border-amber-500/60',
          info: 'border-sky-500/60',
        },
      }}
      {...props}
    />
  )
}

function addDismiss(options?: ToastOptions): ToastOptions {
  if (options?.action) return options
  return { ...options, action: { label: 'Dismiss', onClick: () => {} } }
}

const toast = Object.assign(
  (message: Parameters<typeof sonnerToast>[0], options?: ToastOptions) =>
    sonnerToast(message, addDismiss(options)),
  {
    success: (
      message: Parameters<typeof sonnerToast.success>[0],
      options?: Parameters<typeof sonnerToast.success>[1],
    ) => sonnerToast.success(message, addDismiss(options as ToastOptions) as typeof options),
    error: (
      message: Parameters<typeof sonnerToast.error>[0],
      options?: Parameters<typeof sonnerToast.error>[1],
    ) => sonnerToast.error(message, addDismiss(options as ToastOptions) as typeof options),
    warning: (
      message: Parameters<typeof sonnerToast.warning>[0],
      options?: Parameters<typeof sonnerToast.warning>[1],
    ) => sonnerToast.warning(message, addDismiss(options as ToastOptions) as typeof options),
    info: (
      message: Parameters<typeof sonnerToast.info>[0],
      options?: Parameters<typeof sonnerToast.info>[1],
    ) => sonnerToast.info(message, addDismiss(options as ToastOptions) as typeof options),
    message: (
      message: Parameters<typeof sonnerToast.message>[0],
      options?: Parameters<typeof sonnerToast.message>[1],
    ) => sonnerToast.message(message, addDismiss(options as ToastOptions) as typeof options),
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    custom: sonnerToast.custom,
  },
)

export { Toaster, toast }
